// Settings-04 — LLM Provider Keys tab.
//
// Three controls live here:
//
// 1. Managed-AI-keys toggle (broker-wide, persisted on
//    ``Entity.broker_settings.use_benecrm_managed_keys``). When ON,
//    the runtime ignores the broker's BYOK ProviderKey rows and hits
//    Anthropic with the platform's managed key, forced to Sonnet 4.6.
// 2. Per-key "Set as default" action — currently-default key shows
//    a disabled button so it's clear which row is in effect.
// 3. Default-model dropdown — limited to the Tier-1 catalog. Disabled
//    when managed mode is on (the loop hard-pins Sonnet 4.6).

import { useContext, useState } from "react";

import { Button } from "@/design-system";
import { AuthContext } from "@/features/auth/AuthContext";

import { AddProviderKeyModal } from "./AddProviderKeyModal";
import {
  useBeneAIAgentQuery,
  useBrokerSettingsQuery,
  usePatchBrokerSettingsMutation,
  usePatchDefaultModelMutation,
  useProviderKeysQuery,
  useSetDefaultProviderKeyMutation,
} from "./hooks";
import { TIER1_MODEL_SLUGS } from "./types";


function modelLabel(slug: string): string {
  // Light pretty-printing — the underlying slugs are stable so a
  // hand-written lookup is fine; falls back to the slug itself for
  // anything we forget to add.
  const map: Record<string, string> = {
    "claude-sonnet-4-6": "Claude Sonnet 4.6",
    "claude-opus-4-7": "Claude Opus 4.7",
    "claude-sonnet-4-6-bedrock": "Claude Sonnet 4.6 (Bedrock)",
    "claude-opus-4-7-bedrock": "Claude Opus 4.7 (Bedrock)",
    "gpt-5": "OpenAI GPT-5",
    "gpt-5-azure": "OpenAI GPT-5 (Azure)",
    "gemini-2.5-pro": "Gemini 2.5 Pro",
    "gemini-2.5-pro-vertex": "Gemini 2.5 Pro (Vertex)",
  };
  return map[slug] || slug;
}


function providerLabel(p: string, g: string): string {
  return `${p} · ${g}`;
}


export function LLMProviderKeysTab() {
  const auth = useContext(AuthContext);
  const entityId = auth?.currentEntityId ?? null;

  const brokerSettingsQ = useBrokerSettingsQuery(entityId);
  const providerKeysQ = useProviderKeysQuery();
  const agentQ = useBeneAIAgentQuery();

  const patchBrokerSettings = usePatchBrokerSettingsMutation(entityId);
  const setDefaultProviderKey = useSetDefaultProviderKeyMutation();
  const patchDefaultModel = usePatchDefaultModelMutation();

  const [addOpen, setAddOpen] = useState(false);

  const managedOn = Boolean(
    brokerSettingsQ.data?.use_benecrm_managed_keys,
  );
  const providerKeys = providerKeysQ.data?.rows ?? [];
  const currentSlug = agentQ.data?.default_model_slug ?? "claude-sonnet-4-6";

  const onToggleManaged = () => {
    patchBrokerSettings.mutate({
      use_benecrm_managed_keys: !managedOn,
    });
  };

  const onSetDefault = (providerKeyId: string) => {
    setDefaultProviderKey.mutate(providerKeyId);
  };

  const onChangeModel = (e: React.ChangeEvent<HTMLSelectElement>) => {
    patchDefaultModel.mutate({ slug: e.target.value });
  };

  return (
    <div
      data-testid="settings-llm-provider-keys"
      className="flex flex-col gap-5"
    >
      {/* Managed mode toggle */}
      <section className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-4">
        <header>
          <h2 className="text-sm font-semibold text-text-primary">
            Use BeneCRM&rsquo;s managed AI keys
          </h2>
          <p className="mt-1 max-w-xl text-xs text-text-secondary">
            When this is on, BeneAI runs on our managed Anthropic key
            instead of your own provider keys. The model is fixed to
            Claude Sonnet 4.6 while managed mode is active.
          </p>
        </header>
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            data-testid="settings-llm-managed-toggle"
            checked={managedOn}
            disabled={patchBrokerSettings.isPending}
            onChange={onToggleManaged}
          />
          <span>Managed mode {managedOn ? "is on" : "is off"}</span>
        </label>
      </section>

      {/* Default model picker */}
      <section className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-4">
        <header>
          <h2 className="text-sm font-semibold text-text-primary">
            Default model
          </h2>
          <p
            className="mt-1 max-w-xl text-xs text-text-secondary"
            data-testid="settings-llm-default-model-helper"
          >
            {managedOn
              ? "Managed mode uses Sonnet 4.6. Turn managed mode off to pick your own default."
              : "BeneAI picks this model unless a per-conversation override applies."}
          </p>
        </header>
        <select
          data-testid="settings-llm-default-model"
          value={managedOn ? "claude-sonnet-4-6" : currentSlug}
          disabled={managedOn || patchDefaultModel.isPending}
          onChange={onChangeModel}
          className="w-full max-w-xs rounded-md border border-border-subtle bg-canvas px-2 py-1.5 text-sm text-text-primary"
        >
          {TIER1_MODEL_SLUGS.map((slug) => (
            <option key={slug} value={slug}>
              {modelLabel(slug)}
            </option>
          ))}
        </select>
      </section>

      {/* Provider keys table */}
      <section className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-raised p-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">
              Provider keys
            </h2>
            <p className="mt-1 max-w-xl text-xs text-text-secondary">
              One key per provider can be the active default at any
              time. Add a key here, then pick which one BeneAI uses.
            </p>
          </div>
          <Button
            variant="secondary"
            data-testid="settings-llm-add-key-button"
            onClick={() => setAddOpen(true)}
          >
            Add key
          </Button>
        </header>
        <ul
          data-testid="settings-llm-provider-keys-list"
          className="flex flex-col gap-2"
        >
          {providerKeys.length === 0 && (
            <li className="text-xs text-text-tertiary">
              No provider keys configured yet.
            </li>
          )}
          {providerKeys.map((row) => (
            <li
              key={row.provider_key_id}
              data-testid={`settings-llm-key-row-${row.provider_key_id}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-canvas px-3 py-2"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-text-primary">
                  {row.display_name}
                </span>
                <span className="text-xs text-text-tertiary">
                  {providerLabel(row.provider, row.gateway)}
                  {row.is_default ? " · default" : ""}
                </span>
              </div>
              <Button
                variant="secondary"
                data-testid={`settings-llm-key-set-default-${row.provider_key_id}`}
                disabled={
                  row.is_default
                  || !row.is_active
                  || setDefaultProviderKey.isPending
                }
                onClick={() => onSetDefault(row.provider_key_id)}
              >
                {row.is_default ? "Default" : "Set as default"}
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <AddProviderKeyModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}
